import { useEffect, useMemo, useState } from "react";
import { Service } from "../../../models/ServiceModels";
import { Branch } from "../../../models/BranchModels";
import { resolveImageUrl } from "../../../utils/ImageUrl";
import "./ServiceModal.css";

export type ServiceModalMode = "create" | "edit";

interface ServiceModalProps {
  isOpen: boolean;
  mode: ServiceModalMode;
  service: Service | null;
  branches: Branch[];
  submitting: boolean;
  onClose: () => void;
  onSubmit: (form: ServiceFormValues) => Promise<void>;
}

export interface ServiceFormValues {
  name: string;
  category: string;
  description: string;
  price: string;
  currency: string;
  durationMinutes: string;
  sortOrder: string;
  isActive: boolean;
  branchIds: string[];
  imageFile: File | null;
}

const SERVICE_IMAGE_MAX_BYTES = 150 * 1024;

const emptyForm: ServiceFormValues = {
  name: "",
  category: "",
  description: "",
  price: "0",
  currency: "BDT",
  durationMinutes: "30",
  sortOrder: "0",
  isActive: true,
  branchIds: [],
  imageFile: null,
};

const fromService = (service: Service): ServiceFormValues => ({
  name: service.name,
  category: service.category || "",
  description: service.description || "",
  price: String(service.price ?? 0),
  currency: service.currency || "BDT",
  durationMinutes: String(service.durationMinutes ?? 30),
  sortOrder: String(service.sortOrder ?? 0),
  isActive: service.isActive,
  branchIds: service.branches.map((b) => b.branchId),
  imageFile: null,
});

type TextKey = Exclude<
  keyof ServiceFormValues,
  "isActive" | "branchIds" | "imageFile"
>;

const ServiceModal = ({
  isOpen,
  mode,
  service,
  branches,
  submitting,
  onClose,
  onSubmit,
}: ServiceModalProps) => {
  const [form, setForm] = useState<ServiceFormValues>(emptyForm);
  const [fileError, setFileError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setFileError(null);
    if (mode === "create") {
      setForm({
        ...emptyForm,
        branchIds: branches.map((branch) => branch.id),
      });
    } else if (service) {
      setForm(fromService(service));
    }
  }, [isOpen, mode, service, branches]);

  const imagePreview = useMemo(() => {
    if (form.imageFile) return URL.createObjectURL(form.imageFile);
    return service?.imageUrl ? resolveImageUrl(service.imageUrl) : undefined;
  }, [form.imageFile, service?.imageUrl]);

  useEffect(() => {
    if (!imagePreview?.startsWith("blob:")) return;
    const url = imagePreview;
    return () => URL.revokeObjectURL(url);
  }, [imagePreview]);

  if (!isOpen) return null;

  const setField = (key: TextKey, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const toggleBranch = (branchId: string) => {
    setForm((prev) => ({
      ...prev,
      branchIds: prev.branchIds.includes(branchId)
        ? prev.branchIds.filter((id) => id !== branchId)
        : [...prev.branchIds, branchId],
    }));
  };

  const handleImageFileChange = (file: File | null) => {
    setForm((prev) => ({ ...prev, imageFile: file }));
    if (file && file.size > SERVICE_IMAGE_MAX_BYTES) {
      setFileError(
        `Image must be 150 KB or less (selected file is ${Math.round(file.size / 1024)} KB).`,
      );
    } else {
      setFileError(null);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (
      form.imageFile &&
      form.imageFile.size > SERVICE_IMAGE_MAX_BYTES
    ) {
      setFileError(
        `Image must be 150 KB or less (selected file is ${Math.round(form.imageFile.size / 1024)} KB).`,
      );
      return;
    }
    if (form.branchIds.length === 0) {
      setFileError("Select at least one branch.");
      return;
    }
    onSubmit(form);
  };

  const textField = (
    key: TextKey,
    label: string,
    opts?: { type?: string; full?: boolean },
  ) => (
    <div
      className={`service-form-group ${opts?.full ? "service-form-group--full" : ""}`}
    >
      <label htmlFor={`service-${key}`}>{label}</label>
      <input
        id={`service-${key}`}
        type={opts?.type ?? "text"}
        value={form[key]}
        onChange={(e) => setField(key, e.target.value)}
      />
    </div>
  );

  return (
    <div className="service-modal-overlay">
      <div className="service-modal" onClick={(e) => e.stopPropagation()}>
        <div className="service-modal__header">
          <h3>{mode === "create" ? "Add Service" : "Edit Service"}</h3>
          <button
            className="service-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <form className="service-modal__body" onSubmit={handleSubmit}>
          <div className="service-form-grid">
            <div className="service-form-group service-form-group--full">
              <label htmlFor="service-name">Name *</label>
              <input
                id="service-name"
                type="text"
                value={form.name}
                onChange={(e) => setField("name", e.target.value)}
              />
            </div>

            {textField("category", "Category")}
            {textField("sortOrder", "Sort Order", { type: "number" })}
          </div>

          <div className="service-form-group service-form-group--full">
            <label htmlFor="service-description">Description</label>
            <textarea
              id="service-description"
              rows={3}
              value={form.description}
              onChange={(e) => setField("description", e.target.value)}
            />
          </div>

          <div className="service-form-grid">
            <div className="service-form-group">
              <label htmlFor="service-price">Price *</label>
              <input
                id="service-price"
                type="number"
                step="0.01"
                min="0"
                value={form.price}
                onChange={(e) => setField("price", e.target.value)}
              />
            </div>

            <div className="service-form-group">
              <label htmlFor="service-currency">Currency</label>
              <select
                id="service-currency"
                value={form.currency}
                onChange={(e) => setField("currency", e.target.value)}
              >
                {["BDT", "USD", "EUR", "GBP", "INR", "PKR", "AED", "SAR"].map(
                  (code) => (
                    <option key={code} value={code}>
                      {code}
                    </option>
                  ),
                )}
              </select>
            </div>

            {textField("durationMinutes", "Duration (minutes)", {
              type: "number",
            })}
            <div className="service-form-group">
              <label className="service-form-check">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      isActive: e.target.checked,
                    }))
                  }
                />
                Active
              </label>
            </div>
          </div>

          <div className="service-form-group service-form-group--full">
            <label htmlFor="service-image">Image</label>
            {imagePreview && (
              <img
                className="service-form-preview"
                src={imagePreview}
                alt="Service image preview"
              />
            )}
            <input
              id="service-image"
              type="file"
              accept="image/*"
              onChange={(e) => handleImageFileChange(e.target.files?.[0] ?? null)}
            />
            <p className="service-form-hint">
              Max 150 KB. Preview shows the service image crop (wide banner,
              ≈2.5:1).
            </p>
            {fileError && <p className="service-form-error">{fileError}</p>}
          </div>

          <div className="service-form-group service-form-group--full">
            <label className="service-form-section-label">
              Assign to branches
            </label>
            {branches.length === 0 ? (
              <p className="service-form-hint">
                No branches found. Create a branch first to assign services.
              </p>
            ) : (
              <>
                <div className="service-branch-list">
                  {branches.map((branch) => (
                    <label
                      key={branch.id}
                      className="service-branch-item"
                    >
                      <input
                        type="checkbox"
                        checked={form.branchIds.includes(branch.id)}
                        onChange={() => toggleBranch(branch.id)}
                      />
                      {branch.name}
                    </label>
                  ))}
                </div>
                {form.branchIds.length === 0 && (
                  <p className="service-form-error">
                    Select at least one branch.
                  </p>
                )}
              </>
            )}
          </div>

          <div className="service-modal__actions">
            <button
              type="button"
              className="service-btn service-btn--secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="service-btn service-btn--primary"
              disabled={
                submitting ||
                !form.name.trim() ||
                form.branchIds.length === 0 ||
                !!fileError
              }
            >
              {submitting
                ? "Saving..."
                : mode === "create"
                  ? "Add Service"
                  : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ServiceModal;
